import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, CalendarClock, Settings, Wand2, Info, Copy, Megaphone } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import PageHeader from '../components/PageHeader';
import WeekNavigator from '../components/WeekNavigator';
import ScheduleGrid from '../components/ScheduleGrid';
import ShiftPickerPopover from '../components/ShiftPickerPopover';
import TotalsStrip from '../components/TotalsStrip';
import AutoFillConfirmModal from '../components/AutoFillConfirmModal';
import CopyWeekModal from '../components/CopyWeekModal';
import OfferShiftModal from '../components/OfferShiftModal';
import OfferShiftStatusModal from '../components/OfferShiftStatusModal';
import MonthOverview from '../components/MonthOverview';
import ExportCsvButton from '../components/ExportCsvButton';
import { computeTotals } from '../utils/computeTotals';
import { buildShiftMap, buildLeaveMap } from '../utils/scheduleMaps';
import { getMonthCalendarGrid, computeDailyCosts } from '../utils/monthGrid';
import { downloadScheduleCsv } from '../utils/downloadScheduleCsv';
import { useSubscription } from '../contexts/SubscriptionContext';
import apiClient from '../api/axios';

function getMondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = (day + 6) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function toYMD(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseYMD(str) {
  const [y, m, d] = str.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function getWeekDays(monday) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return d;
  });
}

// Single body builder for both POST and PATCH — always sends the complete target state.
function buildApiBody(intent, scheduleId, employeeId, shiftDate) {
  const base = { scheduleId, employeeId, shiftDate };
  // Story 15 B3 — carried through from the owner's override confirm, if any.
  if (intent.overrideLeaveConflict) base.overrideLeaveConflict = true;
  switch (intent.type) {
    case 'template':
      return { ...base, shiftTemplateId: intent.templateId, startTime: null, endTime: null, isOff: false };
    case 'custom':
      return { ...base, shiftTemplateId: null, startTime: intent.startTime, endTime: intent.endTime, isOff: false };
    case 'off':
      return { ...base, shiftTemplateId: null, startTime: null, endTime: null, isOff: true };
    default:
      return base;
  }
}

// Builds a complete optimistic ShiftResponse-shaped object before the API call returns.
function buildOptimisticShift(oldShift, intent, scheduleId, employeeId, shiftDate, templateMap) {
  const id = oldShift?.id ?? `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const base = { id, scheduleId, employeeId, shiftDate, notes: oldShift?.notes ?? null };

  switch (intent.type) {
    case 'template': {
      const tpl = templateMap.get(intent.templateId) ?? {};
      return {
        ...base,
        shiftTemplateId: intent.templateId,
        shiftTemplateLabel: tpl.label || tpl.name || tpl.shiftName || null,
        startTime: tpl.startTime ?? null,
        endTime: tpl.endTime ?? null,
        crossesMidnight: tpl.crossesMidnight ?? false,
        isOff: false,
      };
    }
    case 'custom':
      return {
        ...base,
        shiftTemplateId: null,
        shiftTemplateLabel: null,
        startTime: intent.startTime,
        endTime: intent.endTime,
        crossesMidnight: intent.endTime <= intent.startTime,
        isOff: false,
      };
    case 'off':
      return {
        ...base,
        shiftTemplateId: null,
        shiftTemplateLabel: null,
        startTime: null,
        endTime: null,
        crossesMidnight: false,
        isOff: true,
      };
    default:
      return base;
  }
}

const ButtonSpinner = () => (
  <svg className="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

const PageSpinner = () => (
  <svg className="w-6 h-6 animate-spin text-orange-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function Scheduler() {
  const { t, i18n } = useTranslation('scheduler');
  const { t: tCommon } = useTranslation('common');
  const { isGated, status: subscriptionStatus } = useSubscription();
  const subscribedPlan = subscriptionStatus?.subscribedPlan ?? null;
  // Story 20-D — fail-closed like SubscriptionGateService.isPlanAtLeast: a null plan is treated
  // as not-eligible, not as "unknown/allow".
  const isGrowthPlus = subscribedPlan === 'GROWTH' || subscribedPlan === 'ENTERPRISE';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initWeek = () => {
    const param = searchParams.get('weekStart');
    if (param) {
      const parsed = parseYMD(param);
      if (parsed) return getMondayOf(parsed);
    }
    return getMondayOf(new Date());
  };

  const [weekStart, setWeekStart] = useState(initWeek);
  const [employees, setEmployees] = useState([]);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [leaveEntries, setLeaveEntries] = useState([]); // Story 15 B1 — approved-leave overlay for the visible week
  const [scheduleId, setScheduleId] = useState(null);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingCells, setSavingCells] = useState(new Set());
  const [pickerState, setPickerState] = useState(null);
  const [toast, setToast] = useState(null);

  // Story 2d: auto-save status
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [lastSaveFailedAt, setLastSaveFailedAt] = useState(null);
  const [nowTick, setNowTick] = useState(0);

  // Story 2d: auto-fill modal state
  const [autoFillState, setAutoFillState] = useState({ open: false, inFlight: false });

  // Story 72b: copy-week modal state
  const [copyWeekModalOpen, setCopyWeekModalOpen] = useState(false);

  // Story 20-D: offer open shift modal + live status modal state
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null); // { id, data } | null

  // Story 7: publish button in-flight state
  const [publishInFlight, setPublishInFlight] = useState(false);

  // Story 18: CSV export in-flight state
  const [exportInFlight, setExportInFlight] = useState(false);

  // Story 2d: restaurant info (for currency formatting)
  const [restaurant, setRestaurant] = useState(null);

  // Month overview heatmap
  const monthShiftsCache = useRef(new Map()); // weekStart YMD → ShiftResponse[]
  const [monthCosts, setMonthCosts] = useState(null); // Map<YMD, { minutes, costSmallestUnit }>
  const [monthLoading, setMonthLoading] = useState(false);
  // BUG-COST: unfiltered (incl. inactive) employees, used ONLY to build the month-overview
  // rate map. Reports/Dashboard cost (ShiftCostCalculator + findAllByRestaurantId) never filter
  // by active status, so a shift worked before an employee was deactivated still has a rate on
  // that side. `employees` (active-only) stays the source for grid rows / week totals.
  const [employeesForCost, setEmployeesForCost] = useState([]);

  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const shiftMap = useMemo(() => buildShiftMap(shifts), [shifts]);

  // Story 15 B1 — overlay, not mutation: expand each approved-leave range into per-day keys
  // covering the visible week, mirroring shiftMap's keying so lookups stay O(1).
  const leaveMap = useMemo(
    () => buildLeaveMap(leaveEntries, weekDays),
    // weekDays changes identity every render; use weekStart string as proxy
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [leaveEntries, weekStart]
  );

  const templateMap = useMemo(
    () => new Map(shiftTemplates.map((tpl) => [tpl.id, tpl])),
    [shiftTemplates]
  );

  // Computed totals (pure, client-side — no network)
  // Story 15b Item 2 — leaveMap excludes needs-cover conflict shifts from totals/cost/cap.
  const totals = useMemo(
    () => computeTotals(shifts, employees, weekDays, leaveMap),
    // weekDays changes identity every render; use weekStart string as proxy
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shifts, employees, weekStart, leaveMap]
  );

  // Save-status label: recomputes every 10s via nowTick
  const saveStatus = useMemo(() => {
    if (savingCells.size > 0) {
      return { text: t('totals.saving'), color: 'text-amber-600' };
    }
    const isFailed =
      lastSaveFailedAt && (!lastSavedAt || lastSaveFailedAt > lastSavedAt);
    if (isFailed) {
      return { text: t('totals.saveFailed'), color: 'text-red-500' };
    }
    if (lastSavedAt) {
      const secsAgo = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);
      if (secsAgo < 10) return { text: t('totals.savedJustNow'), color: 'text-green-600' };
      if (secsAgo < 60) return { text: t('totals.savedSecondsAgo', { seconds: secsAgo }), color: 'text-green-600' };
      return { text: t('totals.savedMinutesAgo', { minutes: Math.floor(secsAgo / 60) }), color: 'text-green-600' };
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingCells, lastSavedAt, lastSaveFailedAt, nowTick, t]);

  // Auto-clear toast after 5s
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Tick every 10s to refresh "Saved X sec ago" label
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 10000);
    return () => clearInterval(id);
  }, []);

  // Fetch restaurant info once on mount (for currency)
  useEffect(() => {
    apiClient
      .get('/api/restaurants/me')
      .then((r) => setRestaurant(r.data))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const weekStartParam = toYMD(weekStart);
    Promise.all([
      apiClient.get('/api/employees?size=100&includeInactive=true'),
      apiClient.get('/api/shift-templates'),
      apiClient.get(`/api/schedules/week?weekStart=${weekStartParam}`),
      apiClient.get(`/api/schedules/week/leave?weekStart=${weekStartParam}`).catch(() => ({ data: [] })),
    ])
      .then(([empRes, stRes, schedRes, leaveRes]) => {
        const rawEmp = Array.isArray(empRes.data)
          ? empRes.data
          : Array.isArray(empRes.data?.content)
          ? empRes.data.content
          : [];
        setEmployees(rawEmp.filter((e) => e.isActive !== false));
        setEmployeesForCost(rawEmp);

        const stData = Array.isArray(stRes.data) ? stRes.data : [];
        setShiftTemplates(stData.filter((s) => s.active !== false));

        const schedData = schedRes.data;
        setScheduleId(schedData?.id ?? null);
        setScheduleStatus(schedData?.status ?? null);
        const rawShifts = Array.isArray(schedData?.shifts)
          ? schedData.shifts
          : Array.isArray(schedData)
          ? schedData
          : [];
        setShifts(rawShifts);
        setLeaveEntries(Array.isArray(leaveRes.data) ? leaveRes.data : []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [weekStart]);

  // Lightweight shifts-only refresh (used after auto-fill and publish to avoid full-page spinner)
  const refetchShifts = useCallback(async () => {
    const weekStartParam = toYMD(weekStart);
    const schedRes = await apiClient.get(`/api/schedules/week?weekStart=${weekStartParam}`);
    const schedData = schedRes.data;
    setScheduleId(schedData?.id ?? null);
    setScheduleStatus(schedData?.status ?? null);
    const rawShifts = Array.isArray(schedData?.shifts)
      ? schedData.shifts
      : Array.isArray(schedData)
      ? schedData
      : [];
    setShifts(rawShifts);
  }, [weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Keep the heatmap live: sync current week's shifts into the cache whenever
  // shifts or weekStart changes, then fetch any missing weeks for the month.
  useEffect(() => {
    const weekStartYMD = toYMD(weekStart);
    monthShiftsCache.current.set(weekStartYMD, shifts);

    const { allMondays } = getMonthCalendarGrid(weekStart.getFullYear(), weekStart.getMonth());
    const missing = allMondays.filter((m) => !monthShiftsCache.current.has(m));

    if (missing.length === 0) {
      if (employeesForCost.length > 0) {
        setMonthCosts(computeDailyCosts(monthShiftsCache.current, employeesForCost));
      }
      setMonthLoading(false);
      return;
    }

    let cancelled = false;
    setMonthLoading(true);

    Promise.all(
      missing.map((m) =>
        apiClient
          .get(`/api/schedules/week?weekStart=${m}`)
          .then((r) => {
            const s = Array.isArray(r.data?.shifts)
              ? r.data.shifts
              : Array.isArray(r.data)
              ? r.data
              : [];
            monthShiftsCache.current.set(m, s);
          })
          .catch(() => {
            monthShiftsCache.current.set(m, []);
          })
      )
    ).then(() => {
      if (!cancelled) {
        if (employeesForCost.length > 0) {
          setMonthCosts(computeDailyCosts(monthShiftsCache.current, employeesForCost));
        }
        setMonthLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [weekStart, shifts, employeesForCost]);

  const handlePrev = () => {
    const prev = new Date(weekStart);
    prev.setDate(prev.getDate() - 7);
    setWeekStart(prev);
    setSearchParams({ weekStart: toYMD(prev) });
    setPickerState(null);
  };

  const handleNext = () => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + 7);
    setWeekStart(next);
    setSearchParams({ weekStart: toYMD(next) });
    setPickerState(null);
  };

  const handleCellClick = useCallback((employeeId, shiftDate, anchorRect) => {
    if (isGated) return;
    setPickerState({ employeeId, shiftDate, anchorRect });
  }, [isGated]);

  const handleCellAction = (employeeId, shiftDate, intent) => {
    const cellKey = `${employeeId}-${shiftDate}`;
    const oldShift = shiftMap.get(cellKey) ?? null;

    if (intent.type !== 'remove' && !scheduleId) return;
    if (intent.type === 'remove') {
      if (!oldShift || String(oldShift.id).startsWith('temp-')) return;
    }

    // Story 15 B3 — assignment guard: assigning a real work shift (template/custom, not
    // off/remove) to someone with approved leave that day requires an explicit override
    // confirm. The backend re-validates final state on save regardless (CLAUDE.md rule #10)
    // — this is UX only, not the enforcement point.
    const onLeave = leaveMap.has(cellKey);
    if (onLeave && (intent.type === 'template' || intent.type === 'custom') && !intent.overrideLeaveConflict) {
      const emp = employees.find((e) => e.id === employeeId);
      const name = emp?.fullName || emp?.name || `${emp?.firstName ?? ''} ${emp?.lastName ?? ''}`.trim();
      const confirmed = window.confirm(t('leave.overrideConfirm', { name }));
      if (!confirmed) return;
      intent = { ...intent, overrideLeaveConflict: true };
    }

    setSavingCells((prev) => new Set([...prev, cellKey]));

    if (intent.type === 'remove') {
      setShifts((prev) =>
        prev.filter((s) => !(s.employeeId === employeeId && s.shiftDate === shiftDate))
      );
    } else {
      const optimistic = buildOptimisticShift(
        oldShift, intent, scheduleId, employeeId, shiftDate, templateMap
      );
      setShifts((prev) => {
        const filtered = prev.filter(
          (s) => !(s.employeeId === employeeId && s.shiftDate === shiftDate)
        );
        return [...filtered, optimistic];
      });
    }

    const apiCall = async () => {
      try {
        let res;
        if (intent.type === 'remove') {
          await apiClient.delete(`/api/shifts/${oldShift.id}`);
        } else if (oldShift && !String(oldShift.id).startsWith('temp-')) {
          res = await apiClient.patch(
            `/api/shifts/${oldShift.id}`,
            buildApiBody(intent, scheduleId, employeeId, shiftDate)
          );
        } else {
          res = await apiClient.post(
            '/api/shifts',
            buildApiBody(intent, scheduleId, employeeId, shiftDate)
          );
        }

        if (res) {
          setShifts((prev) => {
            const filtered = prev.filter(
              (s) => !(s.employeeId === employeeId && s.shiftDate === shiftDate)
            );
            return [...filtered, res.data];
          });
        }

        setLastSavedAt(new Date());
      } catch (err) {
        setShifts((prev) => {
          const filtered = prev.filter(
            (s) => !(s.employeeId === employeeId && s.shiftDate === shiftDate)
          );
          return oldShift ? [...filtered, oldShift] : filtered;
        });

        setLastSaveFailedAt(new Date());

        const emp = employees.find((e) => e.id === employeeId);
        const name =
          emp?.fullName ||
          emp?.name ||
          `${emp?.firstName ?? ''} ${emp?.lastName ?? ''}`.trim() ||
          '';
        const day = new Date(`${shiftDate}T12:00:00`).toLocaleDateString(
          i18n.language,
          { weekday: 'long' }
        );

        setToast({
          type: 'error',
          message: t('toast.saveFailed', { name, day }),
          retryEmployeeId: employeeId,
          retryShiftDate: shiftDate,
          retryIntent: intent,
        });
      } finally {
        setSavingCells((prev) => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }
    };

    apiCall();
  };

  const handlePick = (intent) => {
    if (!pickerState) return;
    const { employeeId, shiftDate } = pickerState;
    setPickerState(null);
    handleCellAction(employeeId, shiftDate, intent);
  };

  // Auto-fill handlers
  const fireAutoFill = useCallback(async (overwriteExisting) => {
    if (!scheduleId) return;
    setAutoFillState({ open: false, inFlight: true });
    try {
      const res = await apiClient.post(
        `/api/schedules/${scheduleId}/auto-fill`,
        { overwriteExisting }
      );
      await refetchShifts();
      const { shiftsCreated, employeesSkippedNoDefault, shiftsSkippedAlreadyExist } = res.data;

      let message;
      if (shiftsCreated === 0 && shiftsSkippedAlreadyExist === 0) {
        message = t('autoFill.successNothing');
      } else if (shiftsCreated > 0 && employeesSkippedNoDefault === 0) {
        message = t('autoFill.successAllFilled', { filled: shiftsCreated });
      } else {
        message = t('autoFill.successMixed', {
          filled: shiftsCreated,
          skippedExisting: shiftsSkippedAlreadyExist,
          skippedNoDefault: employeesSkippedNoDefault,
        });
      }
      const action = employeesSkippedNoDefault > 0
        ? { label: t('autoFill.setDefaultsLink'), onClick: () => { setToast(null); navigate('/team'); } }
        : null;
      setToast({ type: 'success', message, action });
    } catch {
      setToast({ type: 'error', message: t('autoFill.failed') });
    } finally {
      setAutoFillState({ open: false, inFlight: false });
    }
  }, [scheduleId, refetchShifts, t, navigate]);

  const handleAutoFillClick = () => {
    if (!scheduleId || isGated) return;
    if (shifts.length === 0) {
      fireAutoFill(false);
    } else {
      setAutoFillState({ open: true, inFlight: false });
    }
  };

  // Story 7: publish handler
  const handlePublishClick = useCallback(async () => {
    if (!scheduleId || publishInFlight || isGated) return;
    setPublishInFlight(true);
    try {
      const { data } = await apiClient.post(`/api/schedules/${scheduleId}/publish`);
      await refetchShifts();
      const message = data?.nothingNewToPublish
        ? t('toast.publishNothingNew')
        : data?.staffToNotifyCount > 0
          ? t('toast.publishSuccessWithCount', { count: data.staffToNotifyCount })
          : t('toast.publishSuccess');
      setToast({ type: 'success', message });
    } catch (err) {
      // Story 72 — belt-and-suspenders: the button is disabled once the week is empty, but a
      // shift deleted in another tab/session between render and click can still reach the
      // backend guard, so surface its specific message rather than the generic failure toast.
      const message = err.response?.data?.messageKey === 'error.schedule.publish.empty'
        ? t('toast.publishEmptyWeek')
        : t('toast.publishFailed');
      setToast({ type: 'error', message });
    } finally {
      setPublishInFlight(false);
    }
  }, [scheduleId, publishInFlight, isGated, refetchShifts, t]);

  // Story 18: CSV export handler — deliberately never checks isGated; export must work in
  // every subscription state (DPDP data-portability guarantee).
  // Story 18b: month scope exports the currently-viewed week's month — same month
  // MonthOverview derives from weekStart (its year/month, not a separate picker).
  const handleExportCsv = useCallback(async (format, scope = 'week') => {
    setExportInFlight(true);
    try {
      if (scope === 'month') {
        const monthStart = new Date(weekStart.getFullYear(), weekStart.getMonth(), 1);
        await downloadScheduleCsv(toYMD(monthStart), format, 'month');
      } else {
        await downloadScheduleCsv(toYMD(weekStart), format, 'week');
      }
    } catch {
      setToast({ type: 'error', message: t('export.failed') });
    } finally {
      setExportInFlight(false);
    }
  }, [weekStart, t]);

  // Story 72b: copy-week success — summarize targets[], navigate to the first target week
  // (always lands as DRAFT; publish is the separate step that notifies staff).
  const handleCopyWeekSuccess = useCallback((data) => {
    setCopyWeekModalOpen(false);

    const totals = data.targets.reduce(
      (acc, tg) => ({
        shiftsCopied: acc.shiftsCopied + tg.shiftsCopied,
        skippedInactive: acc.skippedInactive + tg.assignmentsSkippedInactiveEmployee,
        skippedHistory: acc.skippedHistory + tg.assignmentsSkippedHasHistory,
        leaveConflicts: acc.leaveConflicts + tg.leaveConflictsFlagged,
      }),
      { shiftsCopied: 0, skippedInactive: 0, skippedHistory: 0, leaveConflicts: 0 }
    );

    const parts = [
      t('copyWeek.toast.success', { shifts: totals.shiftsCopied, weeks: data.targets.length }),
    ];
    if (totals.skippedInactive > 0) {
      parts.push(t('copyWeek.toast.skippedInactive', { count: totals.skippedInactive }));
    }
    if (totals.skippedHistory > 0) {
      parts.push(t('copyWeek.toast.skippedHistory', { count: totals.skippedHistory }));
    }
    if (totals.leaveConflicts > 0) {
      parts.push(t('copyWeek.toast.leaveConflicts', { count: totals.leaveConflicts }));
    }
    setToast({ type: 'success', message: parts.join(' ') });

    const firstTarget = data.targets[0];
    const firstWeekStart = parseYMD(firstTarget.weekStart);
    if (firstWeekStart) {
      setWeekStart(firstWeekStart);
      setSearchParams({ weekStart: firstTarget.weekStart });
      setPickerState(null);
    }
  }, [t, setSearchParams]);

  const handleOfferCreated = useCallback((data) => {
    setOfferModalOpen(false);
    setActiveOffer({ id: data.id, data });
    setToast({ type: 'success', message: t('offerShift.modal.confirm.created') });
  }, [t]);

  const handleSendClick = useCallback((employee) => {
    const name =
      employee.fullName ||
      employee.name ||
      `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim();
    setToast({
      type: 'info',
      message: t('toast.sendComingSoon', { name }),
    });
    console.log({
      event: 'whatsapp_button_clicked',
      kind: 'individual',
      restaurantId: restaurant?.id,
      employeeId: employee.id,
      ts: new Date().toISOString(),
    });
  }, [t, restaurant]);

  const pickerShift = pickerState
    ? shiftMap.get(`${pickerState.employeeId}-${pickerState.shiftDate}`) ?? null
    : null;

  const pickerIsSaving = pickerState
    ? savingCells.has(`${pickerState.employeeId}-${pickerState.shiftDate}`)
    : false;

  const hasEmployees = employees.length > 0;
  const hasShiftTypes = shiftTemplates.length > 0;
  const currency = restaurant?.currency ?? 'INR';
  const managerWhatsappVerified = restaurant?.managerWhatsappStatus === 'VERIFIED';
  const showManagerNotVerifiedHint = !publishInFlight && !!scheduleId && !managerWhatsappVerified;
  // Story 72 — zero shifts means nothing to publish (an all-OFF week still has Shift rows, so
  // it's not "empty" here; the backend applies the same isOff-inclusive count).
  const hasAnyShift = shifts.length > 0;
  const employeesWithDefault = employees.filter((e) => e.defaultShiftTemplateId != null).length;

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[60] flex items-start gap-3 rounded-lg border px-4 py-3 text-sm shadow-lg max-w-xs ${
            toast.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700'
              : toast.type === 'info'
              ? 'border-blue-200 bg-blue-50 text-blue-800'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {toast.type === 'success' ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
            </svg>
          ) : toast.type === 'info' ? (
            <Info size={16} className="mt-0.5 shrink-0" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            </svg>
          )}
          <div className="flex-1 min-w-0">
            <p className="leading-snug">{toast.message}</p>
            {toast.retryIntent && (
              <button
                type="button"
                onClick={() => {
                  const { retryEmployeeId, retryShiftDate, retryIntent } = toast;
                  setToast(null);
                  handleCellAction(retryEmployeeId, retryShiftDate, retryIntent);
                }}
                className="mt-1.5 text-xs font-semibold underline underline-offset-2 hover:no-underline focus:outline-none"
              >
                {t('toast.retry')}
              </button>
            )}
            {toast.action && (
              <button
                type="button"
                onClick={toast.action.onClick}
                className="mt-1.5 text-xs font-semibold underline underline-offset-2 hover:no-underline focus:outline-none"
              >
                {toast.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="text-current opacity-60 hover:opacity-100 focus:outline-none flex-shrink-0"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={[
          <button
            key="shift-types"
            onClick={() => navigate('/settings/shift-types')}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <Settings size={15} />
            {t('actions.shiftTypes')}
          </button>,
          <button
            key="copy-week"
            onClick={() => setCopyWeekModalOpen(true)}
            disabled={!scheduleId || isGated}
            title={isGated ? tCommon('gatedAction.tooltip') : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Copy size={15} />
            {t('copyWeek.action')}
          </button>,
          <button
            key="offer-shift"
            onClick={() => setOfferModalOpen(true)}
            disabled={isGated || !isGrowthPlus}
            title={isGated ? tCommon('gatedAction.tooltip') : !isGrowthPlus ? t('offerShift.upsellTooltip') : undefined}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Megaphone size={15} />
            {t('offerShift.action')}
          </button>,
          <div key="publish-whatsapp" className="relative flex flex-col items-end">
            <button
              onClick={handlePublishClick}
              disabled={publishInFlight || !scheduleId || !managerWhatsappVerified || isGated || !hasAnyShift}
              title={isGated ? tCommon('gatedAction.tooltip') : !hasAnyShift ? t('actions.emptyWeekHint') : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-green-200 disabled:opacity-50 disabled:cursor-not-allowed transition focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2"
            >
              {publishInFlight ? <ButtonSpinner /> : <WhatsAppIcon size={15} className="text-white" />}
              {t('actions.publishToWhatsapp')}
            </button>
            {showManagerNotVerifiedHint && (
              <button
                onClick={() => navigate('/settings/notifications')}
                className="absolute top-full mt-1.5 text-xs text-gray-500 hover:text-orange-600 underline underline-offset-2 whitespace-nowrap"
              >
                {t('actions.managerNotVerifiedHint')}
              </button>
            )}
          </div>,
          <button
            key="autofill"
            onClick={handleAutoFillClick}
            disabled={autoFillState.inFlight || !scheduleId || isGated}
            title={isGated ? tCommon('gatedAction.tooltip') : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 disabled:opacity-50 disabled:cursor-not-allowed transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
          >
            {autoFillState.inFlight ? <ButtonSpinner /> : <Wand2 size={15} />}
            {t('actions.autoFill')}
          </button>,
          <ExportCsvButton key="export-csv" onExport={handleExportCsv} exporting={exportInFlight} />,
        ]}
      />

      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <WeekNavigator weekStart={weekStart} onPrev={handlePrev} onNext={handleNext} status={scheduleStatus} />
      </div>

      {!loading && hasEmployees && hasShiftTypes && (
        <div className="px-6 py-3 border-b border-gray-100 bg-white">
          <MonthOverview
            weekStart={weekStart}
            monthCosts={monthCosts}
            monthLoading={monthLoading}
            currency={currency}
            onDayClick={(mondayDate) => {
              setWeekStart(mondayDate);
              setSearchParams({ weekStart: toYMD(mondayDate) });
              setPickerState(null);
            }}
          />
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center py-20">
          <PageSpinner />
        </div>
      ) : !hasEmployees ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
          <Users size={48} className="text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">{t('emptyEmployees.title')}</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-sm">{t('emptyEmployees.description')}</p>
          <button
            onClick={() => navigate('/team')}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
          >
            {t('emptyEmployees.cta')}
          </button>
        </div>
      ) : !hasShiftTypes ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-16 text-center">
          <CalendarClock size={48} className="text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-700 mb-2">{t('emptyShiftTypes.title')}</h2>
          <p className="text-sm text-gray-500 mb-6 max-w-sm">{t('emptyShiftTypes.description')}</p>
          <button
            onClick={() => navigate('/settings/shift-types')}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2"
          >
            {t('emptyShiftTypes.cta')}
          </button>
        </div>
      ) : (
        <div className="flex-1 px-6 py-6">
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <ScheduleGrid
              employees={employees}
              weekDays={weekDays}
              shiftTemplates={shiftTemplates}
              shiftMap={shiftMap}
              leaveMap={leaveMap}
              savingCells={savingCells}
              onCellClick={handleCellClick}
              onSendClick={handleSendClick}
              dailyTotals={totals.perDay}
              perEmployeeMinutes={totals.perEmployeeMinutes}
              capWarningEmployeeIds={totals.capWarningEmployeeIds}
              currency={currency}
              cellsDisabled={isGated}
              cellsDisabledTitle={tCommon('gatedAction.tooltip')}
            />
            <TotalsStrip
              totalMinutes={totals.totalMinutes}
              totalCostSmallestUnit={totals.totalCostSmallestUnit}
              emptyCellCount={totals.emptyCellCount}
              offCellCount={totals.offCellCount}
              currency={currency}
              saveStatus={saveStatus}
            />
          </div>
        </div>
      )}

      <ShiftPickerPopover
        pickerState={pickerState}
        shift={pickerShift}
        shiftTemplates={shiftTemplates}
        templateMap={templateMap}
        isSaving={pickerIsSaving}
        onPick={handlePick}
        onClose={() => setPickerState(null)}
      />

      <AutoFillConfirmModal
        open={autoFillState.open}
        shiftCount={shifts.length}
        inFlight={autoFillState.inFlight}
        employeesWithDefault={employeesWithDefault}
        totalEmployees={employees.length}
        onKeepExisting={() => fireAutoFill(false)}
        onReplaceAll={() => fireAutoFill(true)}
        onCancel={() => setAutoFillState({ open: false, inFlight: false })}
        onGoToTeam={() => { setAutoFillState({ open: false, inFlight: false }); navigate('/team'); }}
      />

      <CopyWeekModal
        open={copyWeekModalOpen}
        sourceWeekStart={weekStart}
        onClose={() => setCopyWeekModalOpen(false)}
        onSuccess={handleCopyWeekSuccess}
      />

      <OfferShiftModal
        open={offerModalOpen}
        shiftTemplates={shiftTemplates}
        onClose={() => setOfferModalOpen(false)}
        onCreated={handleOfferCreated}
      />

      <OfferShiftStatusModal
        offerId={activeOffer?.id ?? null}
        initialData={activeOffer?.data ?? null}
        onClose={() => setActiveOffer(null)}
        onClaimed={refetchShifts}
      />
    </div>
  );
}

export default Scheduler;